/**
 * Station Vigie dans l'arène : coque de collision (boîtes voxelisées du modèle, data/stationHull.json) et bras
 * télescopique qui relie la baie d'amarrage au bout du moyeu. Partagé par la collision et le rendu.
 */
import type { ArenaData, DockData, GameData } from '../../data';
import { Quat, Vec3 } from '../core/math';
import type { WorldBox } from '../encounter/dock';

export interface StationArm {
  /** Départ sur la superstructure de la baie, arrivée au collier du moyeu (monde). */
  from: Vec3;
  to: Vec3;
  radius: number;
}

export const ARM_RADIUS = 7;

function pose(arena: ArenaData) {
  const s = arena.station!;
  return { position: Vec3.from(s.position), rotation: Quat.from(s.rotation) };
}

/** Bras de la baie vers la station ; null sans station dans l'arène. */
export function stationArm(data: GameData, arena: ArenaData = data.arena, bay: DockData = data.station): StationArm | null {
  if (!arena.station) return null;
  const { position, rotation } = pose(arena);
  const top = Math.max(...bay.parts.map((p) => p.center[1]! + p.half[1]!));
  const mount = bay.armMount ? Vec3.from(bay.armMount) : new Vec3(0, top - 4, 0);
  const from = Vec3.from(bay.position).add(Quat.from(bay.rotation).rotate(mount));
  const to = position.add(rotation.rotate(Vec3.from(data.stationHull.armAnchor)));
  return { from, to, radius: ARM_RADIUS };
}

/** Boîtes de collision de la station (monde) et du bras. */
export function stationBoxes(data: GameData, arena: ArenaData = data.arena): WorldBox[] {
  if (!arena.station) return [];
  const { position, rotation } = pose(arena);
  const boxes: WorldBox[] = data.stationHull.boxes.map((b, i) => ({
    id: `vigie-${i}`,
    center: position.add(rotation.rotate(new Vec3(b[0]!, b[1]!, b[2]!))),
    rotation: rotation.clone(),
    half: new Vec3(b[3]!, b[4]!, b[5]!),
  }));
  const arm = stationArm(data, arena);
  if (arm) {
    const axis = arm.to.sub(arm.from);
    boxes.push({
      id: 'vigie-arm',
      center: arm.from.add(axis.scale(0.5)),
      // Boîte dont l'axe local −Z suit le bras.
      rotation: Quat.lookRotation(axis.normalized()),
      half: new Vec3(arm.radius * 0.85, arm.radius * 0.85, axis.length() / 2),
    });
  }
  return boxes;
}
